<template>
  <div>
    <SettingsCard
      :title="$t('settings.crm.master-title')"
      :subtitle="$t('settings.crm.master-subtitle', { readablePlatformName })"
      icon="mdi-account-sync-outline"
      :model-value="isOpen"
      @update:model-value="isOpen = !isOpen"
    >
      <v-row class="mx-0 my-2 mx-md-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ t('settings.crm.master-settings.title') }}
          </div>
          <div class="text-grey">
            {{ t('settings.crm.master-settings.subtitle') }}
          </div>
          <div class="text-grey mt-3">{{ t('settings.crm.master-settings.info') }}<br /></div>
        </v-col>
        <v-col
          class="flex-row pt-0 pb-6 pt-md-4 d-flex align-center justify-md-end"
          cols="12"
          offset-md="1"
          md="5"
        >
          <div class="pr-2 switch-toggle text-grey">
            {{ t('settings.crm.master-settings.switch-off') }}
          </div>
          <v-switch
            v-model="enforceMasterConnection"
            :disabled="!crmProviderEnabled"
            :label="t('settings.crm.master-settings.switch-on')"
            color="primary"
            hide-details
            @update:model-value="(value) => updateMasterCrmConfig(value)"
          />
        </v-col>
        <v-alert
          v-if="
            !crmProviderEnabled ||
            (enforceMasterConnectionChild &&
              (acceptedMasterConnectionChild || acceptedMasterConnectionChild === false)) ||
            companiesWithCrmProvider?.some((company) => company.id === routeParamId) ||
            (!hasChildCompanyId && companiesWithCrmProvider?.length > 0)
          "
          type="info"
          density="compact"
          variant="text"
        >
          <p
            class="pl-0 ma-0"
            v-if="!crmProviderEnabled"
          >
            {{ t('settings.crm.master-settings.crm-not-configured-info') }}
          </p>
          <p
            class="pl-0 ma-0"
            v-else-if="
              hasChildCompanyId && enforceMasterConnectionChild && acceptedMasterConnectionChild
            "
          >
            {{ t('settings.crm.master-settings.crm-accepted-by-child-company') }}
          </p>
          <p
            class="pl-0 ma-0"
            v-else-if="
              hasChildCompanyId &&
              enforceMasterConnectionChild &&
              acceptedMasterConnectionChild === false
            "
          >
            {{ t('settings.crm.master-settings.crm-rejected-by-child-company') }}
          </p>
          <p
            class="pl-0 ma-0"
            v-else-if="
              hasChildCompanyId &&
              companiesWithCrmProvider?.some((company) => company.id === routeParamId)
            "
          >
            <b>{{
              companiesWithCrmProvider?.find((company) => company.id === routeParamId)?.name
            }}</b>
            {{ t('settings.crm.master-settings.have-crm-configuration') }}
          </p>
          <p
            class="pl-0 ma-0"
            v-else-if="!hasChildCompanyId && companiesWithCrmProvider?.length > 0"
          >
            <span
              v-for="(item, index) of companiesWithCrmProvider"
              :key="item.name"
            >
              <b>{{ item.name }}</b>
              <span v-if="Number(index) < companiesWithCrmProvider.length - 1">, </span>
            </span>
            {{ t('settings.crm.master-settings.have-crm-configuration') }}
          </p>
        </v-alert>
      </v-row>
    </SettingsCard>
    <PropstackMultiCompanyParentSection v-if="showPropstackMultiCompanySection" />
  </div>
</template>
<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import SettingsCard from '@/components/SettingsCard.vue'
import { useBranding } from '@/composables/useBranding'
import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import PropstackMultiCompanyParentSection from '@/modules/settings/crmSettings/propstack/PropstackMultiCompanyParentSection.vue'
import UPDATE_MASTER_SETTING_CRM_COMPANY_CONFIG from '@/modules/settings/crmSettings/queries/MasterSettingsCrmCompanyConfig.gql'
import { TrackingPlugin } from '@/plugins/tracking'
import GET_CHILD_CRM_COMPANIES_WITH_PROVIDER from '@/queries/GetChildCrmCompaniesWithProvider.gql'
import GET_CHILD_CRM_COMPANY_CONFIG from '@/queries/GetChildCrmCompanyConfig.gql'
import GET_CRM_COMPANY_CONFIG from '@/queries/GetCrmCompanyConfig.gql'

interface AuthService {
  user?: {
    companyId: string
  }
}

const route = useRoute()
const routeParamId = computed(() => route?.params?.id ?? null)
const $tracking = inject<TrackingPlugin>('tracking')
const isOpen = ref(true)
const { readablePlatformName } = useBranding()
const { t } = useI18n()
const auth = inject<AuthService>('auth')
const variables = computed(() => ({
  companyId: routeParamId.value,
}))
const enabled = computed(() => !!routeParamId.value)
const { result: crmConfigData, refetch: refetchCrmConfigData } = useQuery(GET_CRM_COMPANY_CONFIG, {
  companyId: auth?.user?.companyId,
})

const { result: childCompanyWithCrmConfig, refetch: refetchChildCompanyWithCrmConfig } = useQuery(
  GET_CHILD_CRM_COMPANY_CONFIG,
  variables,
  { enabled }
)

const { result: childCompaniesWithCrmWithProvider, refetch: refetchChildCompaniesWithProvider } =
  useQuery(GET_CHILD_CRM_COMPANIES_WITH_PROVIDER, {})

const companiesWithCrmProvider = computed(() => childCompaniesWithCrmWithProvider.value?.config)
const enforceMasterConnectionChild = computed(
  () => childCompanyWithCrmConfig.value?.config?.enforceMasterConnection
)
const enforceMasterConnectionMaster = computed(
  () => crmConfigData?.value?.config?.enforceMasterConnection
)
const acceptedMasterConnectionChild = computed(
  () => childCompanyWithCrmConfig.value?.config?.acceptedMasterConnection
)
const enforceMasterConnection = computed(() =>
  routeParamId.value
    ? enforceMasterConnectionChild.value
    : (enforceMasterConnectionMaster.value ?? false)
)
const crmProviderEnabled = computed(
  () => !!crmConfigData?.value?.config?.crmProvider && crmConfigData?.value?.config?.isActive
)
const hasChildCompanyId = computed(() => !!routeParamId.value)

const { isFeatureActive } = useFeature()

const showPropstackMultiCompanySection = computed(
  () =>
    isFeatureActive(featureNames.PROPSTACK_MULTI_COMPANY) &&
    crmConfigData?.value?.config?.crmProvider === 'PROPSTACK' &&
    !!crmConfigData?.value?.config?.isActive
)

const {
  mutate: createOrUpdateCrmCompanyConfig,
  onError: onErrorCreateOrUpdateCrmCompanyConfig,
  onDone: onDoneCreateOrUpdateCrmCompanyConfig,
} = useMutation(UPDATE_MASTER_SETTING_CRM_COMPANY_CONFIG)
const updateMasterCrmConfig = async (value) => {
  await createOrUpdateCrmCompanyConfig({
    input: {
      enforceMasterConnection: value,
      isMasterView: route.path.startsWith('/master'),
      childCompanyId: routeParamId.value,
    },
  })
  $tracking.event(
    'Settings',
    'Clicked',
    'Create or Update CRM Company Config - enforceMasterConnection'
  )
}
onDoneCreateOrUpdateCrmCompanyConfig(async () => {
  const refetchPromises = [refetchCrmConfigData(), refetchChildCompaniesWithProvider()]
  if (routeParamId.value) {
    refetchPromises.push(refetchChildCompanyWithCrmConfig())
  }
  await Promise.all(refetchPromises)
  showSnackbarMessage('success', t('alerts.settings.crm.master-success'))
})
onErrorCreateOrUpdateCrmCompanyConfig((error: any) => {
  showSnackbarMessage('error', t('alerts.settings.crm.master-error'))
  Sentry.captureException(error, {
    tags: {
      component: 'MasterCrmSettings',
      action: 'CREATE_OR_UPDATE_CRM_COMPANY_CONFIG enable/disable enforceMasterConnection',
    },
  })
})
</script>
